import { ReactNode, Ref, useEffect, useRef, useState } from "react";
import { Box, Button } from "@mui/material";
import EmptyState from "global/components/EmptyState/EmptyState";
import strings from "global/constants/StringConstants";
import { fillCopy } from "helpers/methods";
import { maskedContent } from "helpers/maskedContent";
import { filterChanges, nextShownCount, pageCountLabel } from "../ContractCompareHelper";
import {
  CARDS_PER_PAGE,
  CompareFilterKey,
  CompareNotice,
  CompareResult,
  CompareSide,
} from "../ContractCompareTypesAndValidation";
import styles from "../ContractCompare.styles";
import t from "../ContractCompare.tokens";
import ChangeCard from "./ChangeCard";
import {
  AlertTriangleGlyph,
  ArrowRightGlyph,
  ChevronDownGlyph,
  CheckGlyph,
  CodeGlyph,
  FileGlyph,
  FileTextGlyph,
  HourglassGlyph,
  ImageGlyph,
  ListGlyph,
  PencilGlyph,
  PilcrowGlyph,
  RefreshGlyph,
  ScanGlyph,
  TableGlyph,
  UploadGlyph,
} from "./ContractCompareIcons";
import KindTile from "./KindTile";
import OutcomePanel, { PanelAction } from "./OutcomePanel";

const page = strings.contractCompare.page;

const PANEL_ICON = { width: t.panel.icon, height: t.panel.icon };
const NOTICE_ICON = { width: t.result.notice.icon, height: t.result.notice.icon };
const BUTTON_ICON = { width: t.button.icon, height: t.button.icon };

const NOTICE_ICONS: Record<string, ReactNode> = {
  images_differ: <ImageGlyph sx={NOTICE_ICON} />,
  ocr_text: <ScanGlyph sx={NOTICE_ICON} />,
  form_fields_not_compared: <FileTextGlyph sx={NOTICE_ICON} />,
  clauses_by_paragraph: <ListGlyph sx={NOTICE_ICON} />,
  tracked_changes: <PencilGlyph sx={NOTICE_ICON} />,
  hidden_characters: <PilcrowGlyph sx={NOTICE_ICON} />,
  tables_not_compared: <TableGlyph sx={NOTICE_ICON} />,
  ai_unavailable: <CodeGlyph sx={NOTICE_ICON} />,
  changes_capped: <AlertTriangleGlyph sx={NOTICE_ICON} />,
};

/** One side of the strip: the kind tile, the file name, and "6 pages · My contracts" under it. */
const StripSide = ({ eyebrow, side, fallbackName }: { eyebrow: string; side: CompareSide | null; fallbackName: string }) => {
  const name = side?.fileName || fallbackName;
  const where = side ? (side.source === "library" ? page.sourceLibrary : page.sourceUpload) : "";
  const meta = [side ? pageCountLabel(side.pages) : "", where].filter(Boolean).join(page.separator);
  return (
    <Box sx={styles.stripSide} {...maskedContent}>
      <Box component="span" sx={styles.eyebrow}>
        {eyebrow}
      </Box>
      <Box sx={styles.stripName}>
        <KindTile fileName={name} small />
        <Box component="span" dir="auto" sx={styles.stripFile}>
          {name}
        </Box>
      </Box>
      {meta && (
        <Box component="span" sx={styles.fileMeta}>
          {meta}
        </Box>
      )}
    </Box>
  );
};

const Notices = ({ notices }: { notices: CompareNotice[] }) => {
  if (!notices.length) return null;
  return (
    <Box component="ul" sx={styles.notices} aria-label={page.noticesLabel}>
      {notices.map((notice) => (
        <Box component="li" key={notice.code} sx={styles.notice(notice.code === "changes_capped")}>
          <Box component="span" sx={{ display: "flex", flexShrink: 0, pt: "2px" }} aria-hidden="true">
            {NOTICE_ICONS[notice.code] ?? <AlertTriangleGlyph sx={NOTICE_ICON} />}
          </Box>
          <span>{notice.text}</span>
        </Box>
      ))}
    </Box>
  );
};

/** The changes: filter chips with the whole set's counts, the legend, 50 cards, then "Show more". */
const ChangeList = ({ result }: { result: CompareResult }) => {
  const [filter, setFilter] = useState<CompareFilterKey>("all");
  const [shown, setShown] = useState(CARDS_PER_PAGE);
  // "Show more" puts focus on the first new card: the route does not change, so nothing else would announce it.
  const focusIndex = useRef<number | null>(null);

  const available = filterChanges(result.changes, filter);
  const visible = available.slice(0, shown);

  useEffect(() => {
    if (focusIndex.current === null) return;
    const card = document.getElementById(`compare-card-${focusIndex.current}`);
    focusIndex.current = null;
    card?.focus();
  }, [shown]);

  const choose = (key: CompareFilterKey) => {
    setFilter(key);
    setShown(CARDS_PER_PAGE);
  };

  const showMore = () => {
    focusIndex.current = visible.length;
    setShown((count) => nextShownCount(count, available.length));
  };

  return (
    <>
      <Box component="ul" sx={styles.filters} aria-label={page.filtersLabel}>
        {result.filters.map((chip) => {
          const active = chip.key === filter;
          return (
            <li key={chip.key}>
              <Box
                component="button"
                type="button"
                sx={styles.filterChip(active)}
                aria-pressed={active}
                onClick={() => choose(chip.key)}
              >
                {chip.label}
                <Box component="span" sx={styles.filterCount(active)}>
                  {chip.count}
                </Box>
              </Box>
            </li>
          );
        })}
      </Box>

      <Box component="p" sx={styles.legend}>
        <span>
          <Box component="span" sx={styles.struck}>
            {page.legendStruck}
          </Box>{" "}
          {page.legendRemoved}
        </span>
        <span>
          <Box component="span" sx={styles.underlined}>
            {page.legendUnderlined}
          </Box>{" "}
          {page.legendAdded}
        </span>
      </Box>

      {available.length === 0 ? (
        <EmptyState size="medium" icon="search" accent="violet" title={page.filterEmptyTitle} />
      ) : (
        <Box component="ol" sx={styles.cards} aria-label={page.cardsLabel}>
          {visible.map((card, index) => (
            <ChangeCard key={card.id || index} card={card} index={index} />
          ))}
        </Box>
      )}

      {available.length > visible.length && (
        <Box sx={styles.moreRow}>
          <span>{fillCopy(page.showing, { shown: visible.length, total: available.length })}</span>
          <Button
            variant="outlined"
            onClick={showMore}
            startIcon={<ChevronDownGlyph sx={BUTTON_ICON} />}
            sx={styles.actionButton}
          >
            {page.showMore}
          </Button>
        </Box>
      )}
    </>
  );
};

interface ResultViewProps {
  result: CompareResult;
  /** The names the user chose, for a side the service did not describe. */
  oldName: string;
  newName: string;
  onReset: () => void;
  /** A stored copy that cannot be used: switch the old side to Upload. */
  onUploadInstead: () => void;
  /** A page could not be read: the same request once more. */
  onRetry: () => void;
  headingRef: Ref<HTMLHeadingElement>;
}

/** Why there is no list: the panel's icon, tone and extra line per `notCompared` reason. */
const notComparedLook = (result: CompareResult): { icon: ReactNode; text?: string } => {
  switch (result.reason) {
    case "no_text_original":
    case "no_text_revised":
      return { icon: <FileGlyph sx={PANEL_ICON} />, text: page.noTextText };
    case "too_large":
      return { icon: <HourglassGlyph sx={PANEL_ICON} />, text: page.tooLargeText };
    case "too_many_scanned_pages":
      return { icon: <ScanGlyph sx={PANEL_ICON} /> };
    case "unreadable_pages":
      return { icon: <AlertTriangleGlyph sx={PANEL_ICON} /> };
    default:
      return { icon: <FileGlyph sx={PANEL_ICON} /> };
  }
};

/**
 * The finished comparison. The heading takes focus when it appears (decision D51). With changes: summary, the two
 * files, the notices, the filter chips and the cards. Without a list: the same header and files, then one panel that
 * says why, never "identical" -- only text was compared.
 */
const ResultView = ({ result, oldName, newName, onReset, onUploadInstead, onRetry, headingRef }: ResultViewProps) => {
  const compareOther: PanelAction = {
    label: page.compareOther,
    onClick: onReset,
    icon: <RefreshGlyph sx={BUTTON_ICON} />,
  };

  const subtitle =
    result.kind === "changes" || result.kind === "noChanges" ? (result.kind === "changes" ? result.summary : result.message) : page.resultNone;

  const body = (() => {
    if (result.kind === "changes") {
      return (
        <>
          <Notices notices={result.notices} />
          <ChangeList result={result} />
        </>
      );
    }
    if (result.kind === "noChanges") {
      return (
        <OutcomePanel
          tone="success"
          icon={<CheckGlyph sx={PANEL_ICON} />}
          title={page.noChangesTitle}
          text={page.noChangesText}
          textBold
          actions={[compareOther]}
        />
      );
    }
    if (result.kind === "differentDocuments") {
      return (
        <OutcomePanel
          tone="neutral"
          icon={<FileGlyph sx={PANEL_ICON} />}
          title={result.message}
          actions={[{ ...compareOther, primary: true }]}
        />
      );
    }
    const look = notComparedLook(result);
    const retry: PanelAction = {
      label: page.retry,
      onClick: onRetry,
      icon: <RefreshGlyph sx={BUTTON_ICON} />,
      primary: true,
    };
    const actions: PanelAction[] =
      result.reason === "unreadable_pages"
        ? [retry, compareOther]
        : result.switchToUpload
          ? [
          { label: page.uploadOldInstead, onClick: onUploadInstead, icon: <UploadGlyph sx={BUTTON_ICON} />, primary: true },
          compareOther,
        ]
      : [{ ...compareOther, primary: true }];
    return <OutcomePanel tone="warning" icon={look.icon} title={result.message} text={look.text} actions={actions} />;
  })();

  return (
    <>
      <Box sx={styles.resultHeader}>
        <Box sx={styles.headerText}>
          <Box component="h1" ref={headingRef} tabIndex={-1} sx={styles.resultTitle}>
            {page.resultTitle}
          </Box>
          <Box component="p" sx={styles.summary} {...maskedContent}>
            {subtitle}
          </Box>
        </Box>
        <Button
          variant="outlined"
          onClick={onReset}
          startIcon={<RefreshGlyph sx={BUTTON_ICON} />}
          sx={styles.actionButton}
        >
          {page.compareOther}
        </Button>
      </Box>

      <Box sx={styles.strip}>
        <StripSide eyebrow={page.eyebrowOld} side={result.original} fallbackName={oldName} />
        <Box sx={styles.stripArrow} aria-hidden="true">
          <ArrowRightGlyph sx={{ width: 20, height: 20 }} />
        </Box>
        <StripSide eyebrow={page.eyebrowNew} side={result.revised} fallbackName={newName} />
      </Box>

      {body}
    </>
  );
};

export default ResultView;
